// src/components/SuraDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  HiUserCircle,
  HiPlay,
  HiLogout,
  HiBookOpen,
  HiSave,
  HiCheckCircle,
  HiStar,
} from 'react-icons/hi';
import { db } from '../firebase';
import { ref, get } from 'firebase/database';
import Quiz from './Quiz';
/**
 * Converts a standard YouTube URL into an embeddable format with rel=0 parameter.
 * If isDefault is true, it attempts to start with autoplay=1 and mute=1.
 * @param {string} url - Standard YouTube URL (watch?v=ID) or embed URL.
 * @param {boolean} isDefault - Whether this is the default (first loaded) video.
 * @returns {string} - Embeddable and restricted URL.
 */
const getEmbedUrl = (url, isDefault = false) => {
  const videoIdMatch = url.match(/(?:v=|\/embed\/|youtu\.be\/)([^&?]+)/);

  if (!videoIdMatch) {
    console.warn('Invalid YouTube URL format:', url);
    return url;
  }

  const videoId = videoIdMatch[1];
  const baseUrl = `https://www.youtube.com/embed/${videoId}`;

  // rel=0 (prevents suggested videos). If isDefault, includes autoplay=1 and mute=1.
  const params = `?rel=0${isDefault ? '&autoplay=1&mute=1' : '&autoplay=0'}`;

  return `${baseUrl}${params}`;
};

// Sura and Du'a List (English names used for display)
const suraData = [
  // PRAYER DU'AS (id: 0, default starting video)
  {
    id: 0,
    name: "Subhanaka Du'a",
    arabic:
      'سُبْحَانَكَ اللَّهُمَّ وَبِحَمْدِكَ وَتَبَارَكَ اسْمُكَ وَتَعَالَى جَدُّكَ وَلَا إِلَهَ غَيْرُكَ',
    turkish: 'The Subhanaka Invocation',
    videoUrl: 'https://www.youtube.com/watch?v=nfRplJ7QJIk',
  },
  {
    id: 1,
    name: 'Surah Al-Fatiha',
    arabic: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ',
    turkish: 'The Opening',
    videoUrl: 'https://www.youtube.com/watch?v=aR7yB_x9_G4',
  },
  {
    id: 2,
    name: "At-Tahiyyat Du'a",
    arabic: 'اَلتَّحِيَّاتُ لِلَّهِ وَالصَّلَوَاتُ وَالطَّيِّبَاتُ',
    turkish: 'The Prayer Sitting Invocation',
    videoUrl: 'https://www.youtube.com/watch?v=kiPS87Br7Xo',
  },
  // ADHAN AND IQAMAH
  {
    id: 3,
    name: 'Adhan (Call to Prayer)',
    arabic: 'اَللّٰهُ اَكْبَرُ اَللّٰهُ اَكْبَرُ',
    turkish: 'The Call to Prayer',
    videoUrl: 'https://www.youtube.com/watch?v=QT4j25EG54o',
  },
  {
    id: 4,
    name: 'Iqamah',
    arabic: 'اَللّٰهُ اَكْبَرُ اَللّٰهُ اَكْبَرُ (قَدْ قَامَتِ الصَّلَاةُ)',
    turkish: 'Start of Prayer (Iqamah)',
    videoUrl: 'https://www.youtube.com/watch?v=i1KnO79SkWQ',
  },
  // SHORT SURAHS
  {
    id: 5,
    name: 'Surah Al-Kawthar',
    arabic: 'إِنَّا أَعْطَيْنَاكَ الْكَوْثَرَ',
    turkish: 'Abundance',
    videoUrl: 'https://www.youtube.com/watch?v=HnEJXREZUKE',
  },
  {
    id: 6,
    name: 'Surah Al-Kafirun',
    arabic: 'قُلْ يَا أَيُّهَا الْكَافِرُونَ',
    turkish: 'The Disbelievers',
    videoUrl: 'https://www.youtube.com/watch?v=3jE8WdZveRA',
  },
  {
    id: 7,
    name: 'Surah Al-Ikhlas',
    arabic: 'قُلْ هُوَ اللَّهُ أَحَدٌ',
    turkish: 'Purity/Sincerity',
    videoUrl: 'https://www.youtube.com/watch?v=QrAMXvZ0TdM',
  },
  {
    id: 8,
    name: 'Surah Al-Falaq',
    arabic: 'قُلْ أَعُوذُ بِرَبِّ الْفَلَقِ',
    turkish: 'The Daybreak',
    videoUrl: 'https://www.youtube.com/watch?v=QrAMXvZ0TdM',
  },
  {
    id: 9,
    name: 'Surah An-Nas',
    arabic: 'قُلْ أَعُوذُ بِرَبِّ ٱلنَّاسِ',
    turkish: 'Mankind',
    videoUrl: 'https://www.youtube.com/watch?v=I2dVQHU3b54',
  },
];

export default function SuraDashboard() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [studentName, setStudentName] = useState('Student');
  const [selectedSura, setSelectedSura] = useState(suraData[0]);
  const [loading, setLoading] = useState(true);
  const [initialVideoPlayed, setInitialVideoPlayed] = useState(false);
  const [suraNote, setSuraNote] = useState('');
  const [suraCompletionStatus, setSuraCompletionStatus] = useState({});

  // Local Storage Keys
  const NOTE_STORAGE_KEY = `suraNote_${studentId}_${selectedSura.id}`;
  const COMPLETION_STORAGE_KEY = `suraCompletionStatus_${studentId}`;

  // 1. Hook: Fetch Student Name from Firebase
  useEffect(() => {
    async function fetchStudentName() {
      try {
        const snapshot = await get(ref(db, `students/${studentId}`));
        if (snapshot.exists()) {
          const data = snapshot.val();
          setStudentName(data.name || 'Student');
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error('Failed to fetch student data:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    }
    fetchStudentName();
  }, [studentId, navigate]);

  // 2. Hook: Load Note from Local Storage when Sura changes
  useEffect(() => {
    const savedNote = localStorage.getItem(NOTE_STORAGE_KEY) || '';
    setSuraNote(savedNote);
  }, [NOTE_STORAGE_KEY]);

  // 3. Hook: Load Completion Status from Local Storage
  useEffect(() => {
    if (studentId) {
      const savedStatus = localStorage.getItem(COMPLETION_STORAGE_KEY);
      if (savedStatus) {
        setSuraCompletionStatus(JSON.parse(savedStatus));
      }
    }
  }, [studentId, COMPLETION_STORAGE_KEY]);

  // 4. Hook: Autoplay Attempt Management
  const isDefaultSura = selectedSura.id === suraData[0].id;
  useEffect(() => {
    if (isDefaultSura && !initialVideoPlayed) {
      setInitialVideoPlayed(true);
    }
  }, [isDefaultSura, initialVideoPlayed]);

  // Function to save the current note
  const handleSaveNote = () => {
    try {
      localStorage.setItem(NOTE_STORAGE_KEY, suraNote);
      alert(`Notes for "${selectedSura.name}" saved successfully!`);
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Error: Could not save notes.');
    }
  };

  // Function to toggle Sura completion status
  const handleToggleCompletion = (suraId) => {
    const suraIdStr = String(suraId);

    // Toggle the status
    const newStatus = !suraCompletionStatus[suraIdStr];

    const updatedStatus = {
      ...suraCompletionStatus,
      [suraIdStr]: newStatus,
    };

    setSuraCompletionStatus(updatedStatus);

    // Save to localStorage
    try {
      localStorage.setItem(
        COMPLETION_STORAGE_KEY,
        JSON.stringify(updatedStatus),
      );
      alert(
        `"${suraData.find((s) => s.id === suraId).name}" marked as ${newStatus ? 'MEMORIZED' : 'NOT MEMORIZED'}.`,
      );
    } catch (error) {
      console.error('Error saving completion status:', error);
      alert('Error: Could not save status.');
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleSelectSura = (sura) => {
    setSelectedSura(sura);
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center text-xl text-indigo-600'>
        Loading Dashboard... ⏳
      </div>
    );
  }

  const currentVideoUrl = getEmbedUrl(
    selectedSura.videoUrl,
    isDefaultSura && !initialVideoPlayed,
  );

  return (
    <div className='flex flex-col md:flex-row min-h-screen bg-gray-50'>
      {/* Left Navigation / Sura List */}
      <aside className='w-full md:w-72 bg-white shadow-xl md:shadow-none p-4 md:p-6 border-b md:border-r border-gray-200 sticky top-0 md:h-screen overflow-y-auto'>
        {/* Profile and Title */}
        <div className='flex items-center space-x-3 mb-6 p-2 bg-indigo-50 rounded-lg'>
          <HiUserCircle className='w-10 h-10 text-indigo-600' />
          <div>
            <h2 className='text-xl font-bold text-gray-800 truncate'>
              {studentName}
            </h2>
            <p className='text-sm text-indigo-500'>Memorization Panel</p>
          </div>
        </div>

        {/* Sura Navigation */}
        <div className='mt-8'>
          <h3 className='text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center'>
            <HiBookOpen className='w-4 h-4 mr-2' />
            Prayer Du'as and Surahs
          </h3>
          <nav className='space-y-1'>
            {suraData.map((sura) => {
              const isCompleted = !!suraCompletionStatus[sura.id];

              return (
                <button
                  key={sura.id}
                  onClick={() => handleSelectSura(sura)}
                  className={`flex items-center justify-between w-full p-3 rounded-lg text-left transition ${
                    selectedSura.id === sura.id
                      ? 'bg-indigo-600 text-white font-semibold shadow-md'
                      : isCompleted
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-indigo-600'
                  }`}
                >
                  <span className='text-base flex items-center'>
                    {isCompleted && (
                      <HiCheckCircle className='w-4 h-4 mr-2 text-green-600/80' />
                    )}
                    {sura.name}
                  </span>
                  <HiPlay className='w-5 h-5 ml-2' />
                </button>
              );
            })}
          </nav>
        </div>

        {/* Logout Button */}
        <div className='mt-8 pt-4 border-t border-gray-200'>
          <button
            onClick={handleLogout}
            className='flex items-center space-x-2 text-red-500 hover:text-red-700 transition font-medium w-full p-3 rounded-lg hover:bg-red-50 border border-red-500/20'
          >
            <HiLogout className='w-5 h-5' />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content / Video, Quiz, and Text */}
      <main className='flex-1 p-4 md:p-8 space-y-8 overflow-y-auto'>
        <h1 className='text-3xl font-extrabold text-gray-800 border-b pb-2'>
          {selectedSura.name} Recitation and Memorization
        </h1>

        {/* Video and Quiz Area (Grid on Desktop) */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          {/* 1st Column: Video Player (Reduced Size) */}
          <div className='aspect-video bg-gray-900 rounded-xl shadow-2xl overflow-hidden'>
            <iframe
              className='w-full h-full'
              src={currentVideoUrl}
              title={`${selectedSura.name} Recitation`}
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
              key={currentVideoUrl}
            ></iframe>
          </div>

          {/* 2nd Column: Quiz and Medals */}
          <div className='bg-white p-6 rounded-xl shadow-lg'>
            <h2 className='text-2xl font-bold text-yellow-600 mb-4 flex items-center'>
              <HiStar className='w-6 h-6 mr-2 text-yellow-500' />
              Knowledge Quiz & Medals
            </h2>
            <Quiz
              studentId={studentId}
              suraId={selectedSura.id}
              suraName={selectedSura.name}
            />
          </div>
        </div>

        {/* Sura Info and Memorization Tracking Section */}
        <section className='bg-white p-6 rounded-xl shadow-lg mt-8'>
          <h2 className='text-2xl font-bold text-indigo-600 mb-4'>
            {selectedSura.name} ({selectedSura.turkish})
          </h2>
          <p className='text-gray-600 mb-4'>
            Listen to the text below and track your notes for memorization.
          </p>

          <div className='border-t pt-4'>
            <h3 className='text-xl font-semibold text-gray-700 mb-3'>
              Arabic Text (For Memorization)
            </h3>
            <p className='text-4xl md:text-5xl text-right text-gray-800 leading-relaxed p-4 bg-gray-50 border rounded-lg font-arabic'>
              {selectedSura.arabic}
            </p>
          </div>

          {/* MEMORIZATION STATUS (Checkbox) */}
          <div className='mt-6 border-t pt-4'>
            <h3 className='text-lg font-semibold text-gray-700 mb-2'>
              Memorization Status
            </h3>
            <label className='flex items-center space-x-3 cursor-pointer'>
              <input
                type='checkbox'
                checked={!!suraCompletionStatus[selectedSura.id]}
                onChange={() => handleToggleCompletion(selectedSura.id)}
                className='w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500'
              />
              <span
                className={`text-lg font-medium ${suraCompletionStatus[selectedSura.id] ? 'text-green-600' : 'text-gray-700'}`}
              >
                {selectedSura.name} is Memorized / Reading Complete
              </span>
            </label>
          </div>

          {/* MEMORIZATION NOTES */}
          <div className='mt-6 border-t pt-4'>
            <h3 className='text-lg font-semibold text-gray-700 mb-2'>
              Tracking Notes
            </h3>
            <textarea
              className='w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition'
              placeholder={`Write your memorization status or important notes for "${selectedSura.name}" here...`}
              value={suraNote}
              onChange={(e) => setSuraNote(e.target.value)}
            ></textarea>
            <button
              onClick={handleSaveNote}
              className='mt-3 px-6 py-2 bg-green-600 text-white font-medium rounded-md shadow hover:bg-green-700 transition flex items-center space-x-1'
            >
              <HiSave className='w-5 h-5' />
              <span>Save Notes</span>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
